import { FormattedMessage, useIntl } from "react-intl";
import type { MatchMode } from "./design-codec";
import type { UiMatchMode } from "./match-mode";

// The creation-time mode choice (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@4 §4, spark #136 D3). Formerly a
// standalone modal that *was* the creation act; now a selectable card grid
// embedded as the first section of the new-match settings dialog so the whole
// creation happens in one popup. The mode is picked once for a fresh match and
// becomes read-only after the match has content (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@4: immutable after
// creation).
const MODES: Array<{
  mode: MatchMode;
  title: { id: string; defaultMessage: string };
  slogan: { id: string; defaultMessage: string };
  output: { id: string; defaultMessage: string };
}> = [
  {
    mode: "OPTIMIZE",
    title: { id: "match.modePicker.optimizeTitle", defaultMessage: "Optimize team" },
    slogan: {
      id: "match.modePicker.optimizeSlogan",
      defaultMessage: "Compose the best team from a pool.",
    },
    output: { id: "match.modePicker.optimizeOutput", defaultMessage: "one team" },
  },
  {
    mode: "ASSESS",
    title: { id: "match.modePicker.assessTitle", defaultMessage: "Assess team" },
    slogan: {
      id: "match.modePicker.assessSlogan",
      defaultMessage: "Check a given team against the requirements.",
    },
    output: { id: "match.modePicker.assessOutput", defaultMessage: "verdict + gaps" },
  },
  {
    mode: "RANK",
    title: { id: "match.modePicker.rankTitle", defaultMessage: "Rank candidates" },
    slogan: {
      id: "match.modePicker.rankSlogan",
      defaultMessage: "Rank candidates for an open position.",
    },
    output: { id: "match.modePicker.rankOutput", defaultMessage: "ranked list A/B/C" },
  },
  {
    // The company arm (concept v14, the company matching model). The de/en pair is
    // NOT a literal translation, on purpose and by precedent: the Rank card above is
    // "Kandidaten vergleichen" / "Rank candidates", and this one keeps the same asymmetry with
    // "Unternehmen vergleichen" / "Rank companies". The result word is deliberately the SAME as
    // Rank's ("Rangliste" / "ranked list") — that repetition is decided, not an oversight, which is
    // why the pill on the workflow header names this mode by its card title instead.
    mode: "RANK_COMPANIES",
    title: { id: "match.modePicker.rankCompaniesTitle", defaultMessage: "Rank companies" },
    slogan: {
      id: "match.modePicker.rankCompaniesSlogan",
      defaultMessage:
        "You have a need and want companies sorted by who fits best.",
    },
    output: { id: "match.modePicker.rankCompaniesOutput", defaultMessage: "ranked list" },
  },
];

export function ModeSelect({
  value,
  onChange,
  disabled = false,
  idPrefix,
}: {
  // A resolved mode: only the cards below are pickable, but the read-only rendering in the
  // settings dialog must be able to say "none of these" for a mode this build cannot name.
  value: UiMatchMode;
  onChange: (mode: MatchMode) => void;
  disabled?: boolean;
  // Die Kartengruppe steht in ZWEI Dialogen (Anlegen und Einstellungen). Ohne
  // Präfix trügen beide dieselben Kennungen (UI_CONVENTIONS → Test anchors).
  idPrefix: string;
}) {
  const intl = useIntl();
  return (
    <div
      // Four cards, so a 2x2 block rather than a fourth column. The picker lives in a dialog capped
      // at max-w-2xl (~624px of content), where four columns leave ~147px per card - too narrow for
      // a card whose body is a full sentence, and the slogans are the whole point of the picker.
      // 2x2 also has no orphan row: three cards across would leave the fourth alone on its own line
      // and read as an afterthought rather than a peer.
      className="grid gap-3 @sm:grid-cols-2"
      role="radiogroup"
      aria-label={intl.formatMessage({ id: "match.modePicker.title" })}
    >
      {MODES.map((m) => (
        <ModeCard
          key={m.mode}
          {...m}
          id={`${idPrefix}-${m.mode.toLowerCase()}`}
          selected={m.mode === value}
          onPick={() => onChange(m.mode)}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

function ModeCard({
  title,
  slogan,
  output,
  selected,
  onPick,
  disabled,
  id,
}: (typeof MODES)[number] & {
  selected: boolean;
  onPick: () => void;
  disabled: boolean;
  id: string;
}) {
  return (
    <button
      id={id}
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onPick}
      disabled={disabled}
      className={`box-flat bg-page flex flex-col gap-3 p-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui disabled:cursor-default ${
        selected ? "smycles-selected ring-2 ring-accent-decoration" : "hover:ring-1 hover:ring-ink-ui"
      } ${disabled && !selected ? "opacity-50" : ""}`}
    >
      <span className="font-serif text-smycles-title leading-none">
        <FormattedMessage {...title} />
      </span>
      <span className="text-smycles-meta leading-snug text-ink">
        <FormattedMessage {...slogan} />
      </span>
      <span className="smycles-field-label mt-auto">
        <FormattedMessage id="match.modePicker.outputLabel" defaultMessage="Result" />
        {": "}
        <FormattedMessage {...output} />
      </span>
    </button>
  );
}
